import type { TraceEvent, AgentName } from './types';

// Every trace event becomes one RenderedStep — a short, plain-English sentence
// a medical assistant can parse without any system knowledge. The `detail`
// string is still available behind a click for developers who need the
// underlying endpoint / status code.

export type RenderedStep = {
  icon: string;
  summary: string;
  tone: 'thinking' | 'doing' | 'ok' | 'warn' | 'blocked' | 'handoff' | 'done' | 'waiting' | 'secure';
  detail?: string;
  durationMs?: number;
  agent?: AgentName;
};

const AGENT_LABEL: Record<AgentName, string> = {
  'chief-of-staff': 'Chief of Staff',
  onboarding: 'Onboarding specialist',
  outreach: 'Outreach specialist',
  triage: 'Triage specialist',
  'care-plan': 'Care Plan specialist',
  billing: 'Billing specialist',
  device: 'Device specialist',
  'clinic-admin': 'Clinic Admin specialist',
};

function agentName(agent?: AgentName): string {
  return agent ? AGENT_LABEL[agent] || agent : 'the assistant';
}

function actionTypeLabel(t?: string): string {
  const labels: Record<string, string> = {
    chart_review: 'chart review',
    phone_call_patient: 'phone call with the patient',
    phone_call_caregiver: 'phone call with a caregiver',
    phone_call_provider: 'phone call with a provider',
    care_plan_dev: 'care-plan development',
    care_plan_review: 'care-plan review',
    med_review: 'medication review',
    labs: 'labs review',
  };
  return labels[t || ''] || (t || 'patient care');
}

// Strip Express :param markers and describe a path in clinical terms.
function describePath(method: string, path: string, body: any): string | null {
  const p = (path || '').split('?')[0];
  const pid = body?.patient_id ? `patient ${body.patient_id}` : 'a patient';

  // Surveys
  const surveyMatch = p.match(/^\/survey\/([^/]+)\/send_link$/);
  if (surveyMatch) {
    return `Sending the survey link to ${pid}`;
  }

  // Device readings
  const deviceMatch = p.match(/^\/session\/patient\/([^/]+)\/device-readings/);
  if (deviceMatch) {
    const type = /readingType=BloodPressure/.test(path) ? 'BP' : 'device';
    return `Pulling ${type} readings for patient ${deviceMatch[1]}`;
  }

  switch (`${method} ${p}`) {
    case 'POST /session/patient/list_with_details':
      return 'Scanning the full patient panel';
    case 'POST /session/patient/details':
      return `Pulling up ${pid}'s record`;
    case 'POST /session/patient/create':
      return `Creating a new patient record for MRN ${body?.patient_id || ''}`.trim();
    case 'POST /session/patient/onboard/complete': {
      const consent = body?.consent_status ? ` (consent ${body.consent_status})` : '';
      return `Completing onboarding for ${pid}${consent}`;
    }
    case 'POST /session/patient/contact_action/vapi_call':
      return `Placing a voice call to ${pid}`;
    case 'POST /session/patient/action/log': {
      const dur = body?.duration_minutes ? `${body.duration_minutes} min of ` : '';
      return `Logging ${dur}${actionTypeLabel(body?.action_type)} for ${pid}`;
    }
    case 'POST /session/patient/action/report':
      return `Looking up outreach history for ${pid}`;
    case 'POST /session/patient/care_plan_definition/generate':
      return `Drafting a care plan for ${pid}`;
    case 'POST /session/patient/care_plan_definition/list':
      return `Reading the current care plan for ${pid}`;
    case 'POST /session/patient/care_plan_definition/candidates':
      return `Looking at care-plan candidates for ${pid}`;
    case 'POST /session/patient/care_plan_definition/approve':
      return `Recording your care-plan approvals for ${pid}`;
    case 'POST /session/patient/items/candidates':
      return `Reviewing extracted problems and meds for ${pid}`;
    case 'POST /session/patient/items/approve':
      return `Recording your item approvals for ${pid}`;
    case 'POST /session/patient/vitals':
      return `Pulling vitals for ${pid}`;
    case 'POST /session/patient/submission/most_recent_per_survey':
      return `Checking ${pid}'s most recent survey answers`;
    case 'POST /session/patient/custom_survey/create':
      return `Authoring a custom survey for ${pid}`;
    case 'POST /session/organization/voice_enabled':
      return 'Checking whether voice AI is enabled for your clinic';
    case 'POST /session/organization/supported_languages':
      return "Checking the clinic's supported languages";
    case 'GET /is_patient_id_available_for_user':
      return `Checking whether MRN ${body?.patient_id || ''} is available`.trim();
    default:
      // Fall back to a readable last-segment phrase ("/session/patient/vitals" → "Checking patient vitals")
      const tail = p.replace(/^\/(session|api|ccm|clinic|admin)\//, '').replace(/_/g, ' ');
      return method === 'GET' || method === 'POST'
        ? `Working with ${tail}`
        : null;
  }
}

function describeResult(event: TraceEvent): string {
  if (event.status === 0 || !event.ok) {
    return 'That request ran into a problem';
  }
  // Small status signals — e.g. 204 on onboard/complete
  if (event.status === 204) return 'Saved';
  return 'Got the information I needed';
}

export function humanizeEvent(event: TraceEvent): RenderedStep {
  switch (event.kind) {
    case 'turn_start':
      return {
        icon: '💬',
        summary: `${agentName(event.agent)} is working on your request`,
        tone: 'thinking',
        agent: event.agent,
        detail: event.userMessagePreview
          ? `Your request: “${event.userMessagePreview}”`
          : undefined,
      };
    case 'llm_request':
      if (event.wrapUp) {
        return {
          icon: '🧾',
          summary: 'Writing up what I found so far',
          tone: 'thinking',
          agent: event.agent,
          detail: event.model ? `Using model ${event.model}` : undefined,
        };
      }
      return {
        icon: '🧠',
        summary: 'Thinking about the next step',
        tone: 'thinking',
        agent: event.agent,
        detail: event.model ? `Using model ${event.model}` : undefined,
      };
    case 'llm_response': {
      if (event.toolCallCount > 0) {
        return {
          icon: '📝',
          summary:
            event.toolCallCount === 1
              ? 'Deciding on the next action'
              : `Planning ${event.toolCallCount} actions`,
          tone: 'thinking',
          agent: event.agent,
          durationMs: event.durationMs,
          detail: event.contentPreview ? `Reasoning: “${event.contentPreview}”` : undefined,
        };
      }
      return {
        icon: '💡',
        summary: 'Composing the answer',
        tone: 'thinking',
        agent: event.agent,
        durationMs: event.durationMs,
        detail: event.contentPreview ? `Draft: “${event.contentPreview}”` : undefined,
      };
    }
    case 'tool_call': {
      const desc = describePath(event.method, event.path, safeParse(event.bodyPreview));
      return {
        icon: '🔎',
        summary: desc || 'Running a quick lookup',
        tone: 'doing',
        agent: event.agent,
        detail: `${event.method} ${event.path}`,
      };
    }
    case 'tool_result':
      return {
        icon: event.ok ? '✅' : '⚠️',
        summary: describeResult(event),
        tone: event.ok ? 'ok' : 'warn',
        agent: event.agent,
        durationMs: event.durationMs,
        detail: `HTTP ${event.status}${event.method ? ` · ${event.method} ${event.path}` : ''}`,
      };
    case 'delegation_start':
      return {
        icon: '🤝',
        summary: `Handing off to ${agentName(event.specialist as AgentName)}`,
        tone: 'handoff',
        agent: event.agent,
        detail: event.task ? `Task: “${event.task}”` : undefined,
      };
    case 'middleware_block':
      return {
        icon: '⛔',
        // The backend middleware already writes the MA-friendly sentence.
        summary: event.message || 'I held off on that action',
        tone: 'blocked',
        agent: event.agent,
        detail: event.reason ? `(${event.reason})` : undefined,
      };
    case 'middleware_translate':
      return {
        icon: '⚠️',
        summary: event.message || "The action didn't go through",
        tone: 'warn',
        agent: event.agent,
        detail: event.originalStatus ? `Underlying error: ${event.originalStatus}` : undefined,
      };
    case 'approval_pause':
      return {
        icon: '⏸',
        summary: 'Waiting for your approval before doing this',
        tone: 'waiting',
        agent: event.agent,
        detail: `${event.method || ''} ${event.path || ''}`.trim() || undefined,
      };
    case 'redaction_stats':
      return {
        icon: '🔒',
        summary: `Hid ${event.mintCount} patient identifier${event.mintCount === 1 ? '' : 's'} from the AI while it reasoned`,
        tone: 'secure',
        agent: event.agent,
        detail: `Resolved ${event.resolveCount} back to real IDs before showing them to you`,
      };
    case 'metric_query':
      return {
        icon: '📊',
        summary: `Computed ${String(event.metric || 'a metric').replace(/_/g, ' ')}`,
        tone: 'ok',
        agent: event.agent,
        detail: [event.period, event.granularity && event.granularity !== 'total' ? `by ${event.granularity}` : null, `${event.rows} rows`]
          .filter(Boolean)
          .join(' · '),
      };
    case 'table_query':
      return {
        icon: '📋',
        summary: `Built a table from ${event.matched ?? event.returned ?? 0} matching rows`,
        tone: event.error ? 'warn' : 'ok',
        agent: event.agent,
        durationMs: event.durationMs,
        detail: event.error ? `Error: ${event.error}` : event.source ? `Source: ${event.source}` : undefined,
      };
    case 'question':
      return {
        icon: '❓',
        summary: 'Asked you to clarify before going on',
        tone: 'waiting',
        agent: event.agent,
        detail: event.question || undefined,
      };
    case 'cancelled':
      return { icon: '⏹', summary: 'Stopped at your request', tone: 'warn', agent: event.agent };
    case 'budget_exhausted':
      return {
        icon: '⏱',
        summary:
          event.reason === 'time'
            ? 'Reached the time limit for this request'
            : 'Reached the step limit for this request',
        tone: 'warn',
        agent: event.agent,
        detail:
          typeof event.elapsedMs === 'number'
            ? `Stopped after ${Math.round(event.elapsedMs / 1000)}s — answering with what I have`
            : 'Answering with what I have',
      };
    case 'delegation_refused':
      return {
        icon: '↩️',
        summary: "That specialist isn't available for this request",
        tone: 'warn',
        agent: event.agent,
        detail: event.reason || undefined,
      };
    case 'turn_end':
      return {
        icon: event.hasApproval ? '🙋' : '✔',
        summary: event.hasApproval
          ? 'Ready for your decision'
          : 'Done — answer is on its way',
        tone: 'done',
        agent: event.agent,
      };
    default:
      return {
        icon: '•',
        summary: String(event.kind || 'step'),
        tone: 'thinking',
        agent: event.agent as AgentName | undefined,
      };
  }
}

function safeParse(value: unknown): any {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
}

export function summariseTrace(events: TraceEvent[]): { count: number; totalMs: number } {
  if (!events || events.length === 0) return { count: 0, totalMs: 0 };
  const first = Date.parse(events[0].at);
  const last = Date.parse(events[events.length - 1].at);
  const totalMs = Number.isFinite(first) && Number.isFinite(last) ? Math.max(0, last - first) : 0;
  const count = events.filter(
    (e) => e.kind !== 'turn_start' && e.kind !== 'turn_end'
  ).length;
  return { count, totalMs };
}
